import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { FaVideo, FaHeadphones, FaFileAlt, FaDumbbell, FaCheckCircle } from "react-icons/fa";

import { buyCourse as purchaseCourse, courseQuote } from "../../api";

// Styles
import "../../styles/CourseDetails.css";

const CourseDetails = () => {
    const {courseId} = useParams();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [buying, setBuying] = useState(false);
    const [openLessonId, setOpenLessonId] = useState(null);
    const [lessonBodies, setLessonBodies] = useState({});
    const [lessonError, setLessonError] = useState("");
    const [quote, setQuote] = useState(null);

    const userWalletAddress = localStorage.getItem("user");

    const fetchCourseDetails = useCallback(async () => {
        const qs = userWalletAddress ? `?walletAddress=${encodeURIComponent(userWalletAddress.toLowerCase())}` : "";
        const response = await fetch(`/api/coursedetails/${courseId}${qs}`, {
            method: "GET",
            mode: "cors",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json"
            }
        });

        if (!response.ok) {
            throw new Error(`Server Error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        if (Array.isArray(data) && data.length > 0) {
            setCourse(data[0]);
        } else {
            setCourse(null);
        }
        if (userWalletAddress) {
            try {
                setQuote(await courseQuote(courseId));
            } catch (e) {
                setQuote(null);
            }
        }
    }, [courseId, userWalletAddress]);

    useEffect(() => {
        const load = async () => {
            try {
                await fetchCourseDetails();
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [fetchCourseDetails]);

    const buyCourse = async (id) => {
        if (buying) {
            return;
        }
        setBuying(true);
        try {
            const result = await purchaseCourse(id);
            alert(result.message || "Course purchased!");
            await fetchCourseDetails();
        } catch (err) {
            console.error("Buy Course Error:", err);
            alert(err.message || "Error buying course.");
        } finally {
            setBuying(false);
        }
    };


    const openLesson = async (lessonId) => {
        if (!lessonId) {
            return;
        }
        if (openLessonId === lessonId) {
            setOpenLessonId(null);
            return;
        }
        setOpenLessonId(lessonId);
        setLessonError("");
        if (lessonBodies[lessonId]) {
            return;
        }
        try {
            const response = await fetch(`/api/lessons/${lessonId}`);
            if (!response.ok) {
                throw new Error("Could not load lesson");
            }
            const data = await response.json();
            setLessonBodies((prev) => ({ ...prev, [lessonId]: data.body || "This lesson has no notes yet." }));
        } catch (err) {
            setLessonError(err.message || "Could not load lesson");
        }
    };

    const getLessonIcon = (type) => {
        switch (type) {
            case "video":
                return <FaVideo className="lesson-icon video" />;
            case "audio":
                return <FaHeadphones className="lesson-icon audio" />;
            case "presentation":
                return <FaFileAlt className="lesson-icon presentation" />;
            case "exercise":
                return <FaDumbbell className="lesson-icon exercise" />;
            case "assessment":
                return <FaCheckCircle className="lesson-icon assessment" />;
            default:
                return null;
        }
    };

    if (loading)
        return <p>Loading...</p>;
    if (error)
        return <p>Error: {error}</p>;
    if (!course)
        return <p>No course data available.</p>;

    const price = quote?.price ?? course?.course_price ?? 0;
    const needsPayment = Number(price) > 0 && !quote?.paid;

    return (
            <div className="course-details">
                <h1>{course?.course_title}</h1>
                <p>{course?.course_description}</p>
            
                {course.lessons && course.lessons.length > 0 && (
                                    <p><strong>Total Duration:</strong> {
                                                course.lessons.reduce((total, lesson) => total + lesson.lesson_duration, 0)
                                        } minutes</p>
                                    )}
            
                {course?.booked_by && !needsPayment ? (
                            <div>
                                <p><strong>Progress:</strong> {course?.completion_percentage ?? 0}%</p>
                                <div className="progress-bar">
                                    <div className="progress" style={{width: `${course?.completion_percentage ?? 0}%`}}></div>
                                </div>
                            </div>
                            ) : (
                            <div>
                                <p><strong>Price:</strong> {price} WSD</p>
                                {course?.booked_by && needsPayment && (
                                    <p>This course is reserved, but the {price} WSD payment was not taken. Confirm the transfer in MetaMask to debit your balance.</p>
                                )}
                                <button className="buy-btn" disabled={buying} onClick={() => buyCourse(course.course_id || course.id || courseId)}>
                                    {buying ? "Paying…" : (course?.booked_by ? `Pay ${price} WSD` : "Buy with WSD")}
                                </button>
                            </div>
                            )}
            
                <h2>Lessons</h2>
                <div className="lessons-list">
                    {course?.lessons && course.lessons.length > 0 && course.lessons[0] && course.lessons[0].lesson_id ? (
                                course.lessons.map((lesson) => (
                                        <div
                                            key={lesson.lesson_id}
                                            className={`lesson-card ${lesson.lesson_status || ""} ${openLessonId === lesson.lesson_id ? "open" : ""}`}
                                            onClick={() => openLesson(lesson.lesson_id)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter" || event.key === " ") {
                                                    openLesson(lesson.lesson_id);
                                                }
                                            }}
                                        >
                                            {getLessonIcon(lesson.lesson_type)}
                                            <h3>{lesson.lesson_title}</h3>
                                            <p><strong>Duration:</strong> {lesson.lesson_duration} mins</p>
                                            <p><strong>Type:</strong> {lesson.lesson_type}</p>
                                            {openLessonId === lesson.lesson_id && (
                                                <div className="lesson-body">
                                                    {lessonError ? <p>{lessonError}</p> : (
                                                        <pre>{lessonBodies[lesson.lesson_id] || "Loading lesson notes…"}</pre>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                            ))
                                ) : (
                            <p>No lessons available yet.</p>
                                )}
                </div>
            </div>
            );
};

export default CourseDetails;
